import { IdPrefix } from '@monetize/types/common';
import { ID_PREFIX_TO_CONFIG } from '../constants/common';
import { getAccountDetailRoute } from '../constants/routes';

/**
 * Known ID prefixes, longest first so that regex alternation prefers the more
 * specific match (`invce` over `invc`, `disct` over `disc`).
 */
const RECORD_ID_PREFIXES = (
  Object.keys(ID_PREFIX_TO_CONFIG) as IdPrefix[]
).sort((first, second) => second.length - first.length);

/**
 * Matches any known record ID, e.g. `quot_abc123`. The leading word boundary
 * keeps embedded text like `my_prod_id` from being read as a record reference.
 */
export const RECORD_ID_REGEX = new RegExp(
  `\\b(?:${RECORD_ID_PREFIXES.join('|')})_[A-Za-z0-9]+`,
  'g',
);

/**
 * Whether the input is a record ID like `quot_abc123` — a known ID prefix
 * followed by a non-empty identifier. Prefix matching is case-sensitive,
 * matching the sidebar OmniSearchInput behavior.
 */
export const isEntityIdQuery = (input: string): boolean => {
  const [prefix, ...idParts] = input.trim().split('_');
  return (
    idParts.join('_').length > 0 && Object.hasOwn(ID_PREFIX_TO_CONFIG, prefix)
  );
};

/**
 * Invoice numbers look like `100000034-1` — a sequence number and a revision
 * suffix, never a prefixed record ID. Anchored and digits-only so it cannot
 * swallow an account-name search.
 */
export const INVOICE_NUMBER_REGEX = /^\d+-\d+$/;

/**
 * Whether the input is shaped like an invoice number. This is a cheap *trigger*
 * for the invoice lookup, not a classifier: the backend matches `invoiceNumber`
 * exactly, so a false positive simply returns no rows and the account search
 * that runs alongside it still answers the query.
 */
export const isInvoiceNumberQuery = (input: string): boolean =>
  INVOICE_NUMBER_REGEX.test(input.trim());

/**
 * Account IDs go straight to the account detail page; every other record ID
 * routes to `/<id>`, which the catch-all IdPrefixNavigator resolves to the
 * entity's detail route — including entities whose route needs an API lookup
 * first (payments, credits, subscriptions, and the like).
 */
export const buildRecordRoute = (recordId: string): string => {
  if (recordId.startsWith('acct_')) {
    return getAccountDetailRoute(recordId);
  }
  return `/${recordId}`;
};
